import React, { useState, useEffect } from 'react';
import axios from 'cccisd-axios';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import notification from 'cccisd-notification';
import Table from 'cccisd-table';
import courseResourceQuery from './courseResources.graphql';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

const AllCourseProgress = () => {
    if (!Fortress.hasAccess('app.allCourseProgress')) {
        return (
            <div className="alert alert-danger mt-3">
                <p className="mb-0">You do not have permissions to see this page. Please contact an administrator.</p>
            </div>
        );
    }

    const [courseList, setCourseList] = useState(null);
    const [selectedDeploymentList, setSelectedDeploymentList] = useState([]);
    const [isLoadingTableData, setIsLoadingTableData] = useState(false);
    const [tableData, setTableData] = useState([]);

    async function getTableData() {
        setIsLoadingTableData(true);
        const resp = await axios.post(Boilerplate.route('app.allCourseProgress'), {
            selectedDeploymentList,
        });

        if (resp?.data?.status !== 'success') {
            setTableData([]);
            setIsLoadingTableData(false);
            return notification({ type: 'danger', message: 'Error getting data', data: resp.data, duration: -1 });
        }

        const deploymentIdCourseLabelMap = courseList.reduce((all, curr) => {
            all[curr.deploymentId] = curr.label;
            return all;
        }, {});

        setTableData(
            resp.data.data.map(row => {
                let course = deploymentIdCourseLabelMap[row.deployment_id];
                if (!course) {
                    course = deploymentIdCourseLabelMap[row.parent_deployment_id];
                }
                row.course = course;

                return row;
            })
        );
        setIsLoadingTableData(false);
    }

    useEffect(() => {
        async function getCourses() {
            const resp = await client.query({
                query: courseResourceQuery,
                fetchPolicy: 'network-only',
            });

            setCourseList(
                resp.data.resources.resourceList.map(r => ({
                    id: r.resourceId,
                    label: r.label,
                    deploymentId: (typeof r.metadata === 'string' ? JSON.parse(r.metadata) : r.metadata).deploymentId,
                }))
            );
        }
        getCourses();
    }, []);

    function setAllSelected() {
        setSelectedDeploymentList(courseList.map(res => res.deploymentId));
    }

    useEffect(() => {
        if (courseList && courseList.length > 0) {
            setAllSelected();
        }
    }, [courseList]);

    function toggleSelected(deploymentId) {
        if (selectedDeploymentList.includes(deploymentId)) {
            setSelectedDeploymentList(prev => prev.filter(c => c !== deploymentId));
        } else {
            setSelectedDeploymentList(prev => [...prev, deploymentId]);
        }
    }

    if (!courseList) {
        return <Loader loading />;
    }

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-end alert alert-light">
                <div className={style.selectCourses}>
                    <h5 className="d-inline me-4">
                        Courses {`(${selectedDeploymentList.length}/${courseList.length})`}
                    </h5>
                    <button className="btn btn-xs btn-link me-2" onClick={setAllSelected} type="button">
                        Select All
                    </button>
                    <button className="btn btn-xs btn-link" onClick={() => setSelectedDeploymentList([])} type="button">
                        Deselect All
                    </button>
                    <div className={style.courseToggles}>
                        {courseList.map(c => (
                            <label className="d-block" key={c.id}>
                                <input
                                    onChange={() => toggleSelected(c.deploymentId)}
                                    type="checkbox"
                                    checked={selectedDeploymentList.includes(c.deploymentId)}
                                />
                                {c.label}
                            </label>
                        ))}
                    </div>
                </div>
                <button className="btn btn-primary" type="button" onClick={getTableData}>
                    Get Completion Data
                </button>
            </div>
            {isLoadingTableData && <Loader type="inline" loading />}
            {tableData.length > 0 && !isLoadingTableData && (
                <div className={style.tableContainer}>
                    <Table
                        name="allprogress"
                        data={tableData}
                        rowKey="apId"
                        saveState={false}
                        columns={[
                            { name: 'username', label: 'Username', sort: true, filter: true },
                            { name: 'course', label: 'Course', sort: true, filter: true },
                            {
                                name: 'timepoint',
                                label: 'Attempt',
                                sort: true,
                                filter: true,
                                tooltip:
                                    'Attempt is only relevant for courses that allow users to renew their certificate by resetting progress',
                            },
                            {
                                name: 'started_at',
                                label: 'Started',
                                sort: true,
                                filter: true,
                                filterSettings: {
                                    type: 'date',
                                },
                            },
                            {
                                name: 'last_visited_at',
                                label: 'Last Visit',
                                sort: true,
                                filter: true,
                                filterSettings: {
                                    type: 'date',
                                },
                            },
                            {
                                name: 'completed_at',
                                label: 'Completed',
                                sort: true,
                                filter: true,
                                filterSettings: {
                                    type: 'date',
                                },
                            },
                        ]}
                    />
                </div>
            )}
        </div>
    );
};

export default AllCourseProgress;
